import { v4 as uuid } from 'uuid';
import Debug from 'debug';

import { safeGetError } from '../../../lib/metrics';
import type { Environment } from '../../../types/config';
import { getComposeItem } from '../../abstractions/item';
import {
  clearNotification,
  setErrorNotification,
  setProgressNotification,
} from '../../abstractions/notifications';
import { canSendEmailWithoutToken, isApiSupported, isViewInReadMode } from '../../abstractions/office';
import { OutlookCompose } from '../../abstractions/Outlook';
import type { ComposeBase, OutlookRecipientSet } from '../../abstractions/types';
import { safeIgnore } from '../../lib/ignore';
import type { AuthenticatedApiService } from '../../services/AuthenticatedApi';
import { LOG_TYPE, MetricService } from '../MetricService';
import { getEmailAddressForBixelTransform, getEmailAddressList, validateMessage } from './emailUtils';
import { BixelClient, BixelTransform } from './';

type SendEmailParams = {
  api: AuthenticatedApiService;
  env: Environment;
  clientId: string;
  nativeSend?: boolean;
  transcriptEnforcement?: boolean;
};

/**
 *
 * Add HTML at the cursor in an email message
 */
export async function prependHtmlToEmail(html: string): Promise<boolean> {
  if (isViewInReadMode()) {
    console.warn('Cannot insert while in read mode');
    return Promise.resolve(false);
  }

  const item = getComposeItem();
  return new Promise((resolve, reject) => {
    item.body.setSelectedDataAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        return reject(result.error);
      }
      resolve(true);
    });
  });
}

/**
 *
 * Add bixel tracking and send email
 */
export async function sendEmail({
  api,
  env,
  clientId,
  nativeSend,
  transcriptEnforcement = false,
}: SendEmailParams): Promise<'success' | 'fail' | 'fail:send' | 'fail:track'> {
  const debug = Debug('o365:sendEmail');
  const segment = `send.email${nativeSend ? '-native' : ''}${transcriptEnforcement ? '-transcript' : ''}`;
  let item: ComposeBase;

  try {
    debug('setting progress notification');
    await safeIgnore(() => setProgressNotification('Sending your email'));

    // user will be notified if message is not ready to send.
    debug('validating email');
    item = getComposeItem();
    if (!(await validateMessage(item))) {
      MetricService.yardstick(`${segment}.failure.validation`);
      return 'fail';
    }
  } catch (e) {
    // can't set error notification here, the "item" is busted.
    // TODO: figure out how to communicate with user?
    MetricService.yardstick(`${segment}.failure.item`);
    await MetricService.log(
      'sendEmail(): Error while attempting to access and validate item',
      LOG_TYPE.ERROR,
      safeGetError(e),
    );
    return 'fail';
  }

  debug('initializing Bixel client and transformer');
  const client = new BixelClient({ api, clientId, env });
  await client.loadSendingPool();

  const transform = new BixelTransform({
    addSearchableIdentifier: !isApiSupported('Mailbox', '1.8'),
    clientId,
    pool: client.sendingPool,
  });

  const outlook = new OutlookCompose(item);
  let subject: string;
  let emailId: string;
  let emailAddress: Record<'email' | 'name', string>;
  let recipients: OutlookRecipientSet;
  let isDraft: boolean;
  let body = '';
  let searchableIdentifier = null;

  // urls specified in this email which are reported to bixel
  // (the URLs also determines if an email has a video which
  // is used by BI)
  const urls: string[] = [];

  try {
    debug('loading email data');

    body = await outlook.getBody();
    debug('got body', body.length);

    recipients = await outlook.getRecipients();
    debug('got recipients', {
      to: recipients.to.length,
      cc: recipients.cc.length,
      bcc: recipients.bcc.length,
    });

    emailId = (await outlook.getBixelId()) ?? `outlook365:${uuid()}`;
    emailAddress = getEmailAddressForBixelTransform(recipients);
    debug('using Bixel email id and address', { emailId, emailAddress });

    const transformedBody = transform.transform(body, {
      emailAddress: emailAddress.email,
      emailId,
    });
    debug('transformed body', transformedBody.html.length);

    // copy links so we can pass to bixel
    transformedBody.urls?.forEach((url) => urls.push(url));

    await outlook.setBody(transformedBody.html);
    debug('set body');

    await outlook.setBixelId(emailId);
    debug('set Bixel id');

    // do not save the draft if we're doing native/JavaScript sending.
    if (!nativeSend && !canSendEmailWithoutToken(item)) {
      // Set the searchable identifier for save tracking, if needed.
      outlook.setSearchableIdentifier(transformedBody.searchableIdentifier);

      // if we're modifying a saved draft, start the save process here to allow Outlook maximum
      // time to sync the change to the server while we continue to the next steps.
      isDraft = await outlook.saveIfDraft();
      debug('saved if draft', isDraft);
    } else {
      isDraft = false;
    }

    // Get data only necessary for Bixel.
    subject = await outlook.getSubject();
    debug('got subject', subject);
    searchableIdentifier = transformedBody.searchableIdentifier;
  } catch (e) {
    await safeIgnore(() => {
      return setErrorNotification(`An error occurred while preparing your email: ${(e as any).toString()}`);
    });
    MetricService.yardstick(`${segment}.failure.transform`);
    await MetricService.log(
      'sendEmail(): Unexpected error during email transformation',
      LOG_TYPE.ERROR,
      safeGetError(e),
    );
    // reset changes from the bixel step: set the body back & remove bixel ID
    if (outlook && body) {
      await outlook.setBody(body);
      await outlook.removeBixelId();
    }
    return 'fail:track';
  }

  try {
    debug('notifying bixel of email sent', transform.metrics);
    await client.notifyEmailSent({
      emailId,
      emailAddress: emailAddress.email,
      subject,
      recipientName: emailAddress.name,
      recipients: getEmailAddressList(recipients),
      urls,
    });

    // metric name for the first metric/success metric
    // keep send.email as this is going to metro
    const metric = `send.email.success${transform.metrics.hasVideo ? '.video' : ''}`;

    // figure out if we have videos
    const videoIds: (string | null)[] = transform.metrics.videos ?? [];
    if (videoIds.length === 0) {
      // since we have no IDs, we need to add null for the first metric
      videoIds.push(null);
    }

    // send first success metric and subsequent video metrics
    let sentFirstMetric = false;
    const metricPromises = videoIds.map((videoId) => {
      if (!sentFirstMetric) {
        sentFirstMetric = true;
        debug('send first metric', metric, videoId);
        return MetricService.increment(metric, videoId);
      }
      debug('send subsequent metric', videoId);
      return videoId ? MetricService.increment('send.email.extravideo', videoId) : Promise.resolve();
    });
    await Promise.all(metricPromises);
  } catch (e) {
    await safeIgnore(() => setErrorNotification('An error occurred while notifying Bixel of your email'));
    MetricService.yardstick(`${segment}.failure.tracking`);
    await MetricService.log(
      'sendEmail(): Error while notifying Bixel of send',
      LOG_TYPE.ERROR,
      safeGetError(e),
    );

    // reset changes from the bixel step: set the body back & remove bixel ID
    await outlook.setBody(body);
    await outlook.removeBixelId();

    return 'fail:track';
  }

  if (nativeSend) {
    await MetricService.yardstick(`${segment}.success`);
    return 'success';
  }

  if (canSendEmailWithoutToken(item)) {
    const now = Date.now();
    try {
      debug('sending email via Office JavaScript API');
      await outlook.sendTokenless();
    } catch (error) {
      console.warn('BombBomb: Office Javascript API failed, using fallback...');
      const result = await sendEmailFallbackUsingToken(debug, searchableIdentifier, outlook, segment);
      if (result !== 'success') {
        return result;
      }
    } finally {
      await safeIgnore(() => clearNotification());

      const timeTaken = Date.now() - now;
      await MetricService.yardstick(`${segment}.timing.${isDraft ? 'draft' : 'new'}`, timeTaken, 'timing');
    }
  } else {
    const now = Date.now();
    try {
      debug('sending email via Graph API');
      await outlook.send();
    } catch (e) {
      await safeIgnore(() => setErrorNotification('An error occurred while sending your email'));
      MetricService.yardstick(`${segment}.failure.api`);
      const errorMessage = canSendEmailWithoutToken(item)
        ? 'sendAsync(): Error while sending via Office JavaScript API'
        : 'sendEmail(): Error while sending via Outlook API';
      await MetricService.log(errorMessage, LOG_TYPE.ERROR, safeGetError(e));
      return 'fail:send';
    } finally {
      await safeIgnore(() => clearNotification());

      const timeTaken = Date.now() - now;
      await MetricService.yardstick(`${segment}.timing.${isDraft ? 'draft' : 'new'}`, timeTaken, 'timing');
    }
  }

  debug('closing item');
  item.close();

  await MetricService.yardstick(`${segment}.success`);
  return 'success';
}

async function sendEmailFallbackUsingToken(
  debug: Debug.Debugger,
  searchableIdentifier: string | null,
  outlook: OutlookCompose,
  segment: string,
) {
  // Set the searchable identifier for save tracking, if needed.
  outlook.setSearchableIdentifier(searchableIdentifier);

  // if we're modifying a saved draft, start the save process here to allow Outlook maximum
  // time to sync the change to the server while we continue to the next steps.
  const isDraft = await outlook.saveIfDraft();
  debug('saved if draft', isDraft);

  try {
    debug('sending email via Graph API');
    await outlook.send();
  } catch (e) {
    await safeIgnore(() => setErrorNotification('An error occurred while sending your email'));
    MetricService.yardstick(`${segment}.failure.api`);
    await MetricService.log(
      'sendEmail(): Error while sending via Graph API',
      LOG_TYPE.ERROR,
      safeGetError(e),
    );
    return 'fail:send';
  }

  return 'success';
}
